import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Client } from '../../http';
import { RootState } from '../../app/store';
import {
  Account, AccountsListWithNext, Service, ServicesListWithNext,
} from '../../accounts/models';

export interface CRNAvailability {
  total: number;
  allocated: number;
  quarantined: number;
  available: number;
}

export interface CRNAssignResult {
  customer_id: string;
  email: string;
  crn: string;
  previous_crn?: string;
  created: boolean;
  rotated: boolean;
}

export interface AssignCRNArgs {
  customerId?: string;
  email?: string;
  rotate?: boolean;
}

interface SyncResult {
  account: Account;
  services: Service[];
}

export interface AccountsState {
  accounts: Account[];
  accountsStatus: 'idle' | 'loading' | 'failed';
  nextToken: string | null;

  servicesById: Record<string, Service[]>;
  servicesStatusById: Record<string, 'idle' | 'loading' | 'failed'>;
  syncStatusById: Record<string, 'idle' | 'syncing'>;

  crn: CRNAvailability | null;
  crnStatus: 'idle' | 'loading' | 'failed';

  assignResult: CRNAssignResult | null;
  assignStatus: 'idle' | 'loading' | 'failed';
  assignError: string | null;

  // Server-side exact search (by CRN / email / customer id). null when inactive.
  searchResults: Account[] | null;
  searchQuery: string;
  searchStatus: 'idle' | 'loading' | 'failed';
}

const initialState: AccountsState = {
  accounts: [],
  accountsStatus: 'idle',
  nextToken: null,

  servicesById: {},
  servicesStatusById: {},
  syncStatusById: {},

  crn: null,
  crnStatus: 'idle',

  assignResult: null,
  assignStatus: 'idle',
  assignError: null,

  searchResults: null,
  searchQuery: '',
  searchStatus: 'idle',
};

/** Load the first page of accounts (resets state). */
export const fetchAccounts = createAsyncThunk(
  'accounts/fetchAccounts',
  async () => {
    const response = await Client.get<AccountsListWithNext>('/api/v1/admin/accounts');
    return response.data;
  },
);

/** Append the next page of accounts using the stored nextToken. */
export const fetchMoreAccounts = createAsyncThunk(
  'accounts/fetchMoreAccounts',
  async (_, { getState }) => {
    const { nextToken } = (getState() as RootState).accounts;
    const base = '/api/v1/admin/accounts';
    const url = nextToken ? `${base}?next=${encodeURIComponent(nextToken)}` : base;
    const response = await Client.get<AccountsListWithNext>(url);
    return response.data;
  },
  {
    condition: (_, { getState }) => {
      const state = (getState() as RootState).accounts;
      return state.nextToken !== null && state.accountsStatus !== 'loading';
    },
  },
);

/** Fetch services for one account; skips if already cached or loading. */
export const fetchServicesForAccount = createAsyncThunk(
  'accounts/fetchServicesForAccount',
  async (id: string) => {
    const url = `/api/v1/admin/accounts/${id}/services`;
    const response = await Client.get<ServicesListWithNext>(url);
    return response.data.items ?? [];
  },
  {
    condition: (id, { getState }) => {
      const state = (getState() as RootState).accounts;
      return !state.servicesById[id] && state.servicesStatusById[id] !== 'loading';
    },
  },
);

/**
 * Sync account + services from Chargebee.
 * Only called when the row is expanded and last_synced_at is older than 7 days.
 * On success, updates servicesById and the account's last_synced_at in the list.
 */
export const syncAccount = createAsyncThunk(
  'accounts/syncAccount',
  async (id: string) => {
    const response = await Client.post<SyncResult>(`/api/v1/admin/accounts/${id}/sync`, {});
    return response.data;
  },
  {
    condition: (id, { getState }) => {
      const state = (getState() as RootState).accounts;
      return state.syncStatusById[id] !== 'syncing';
    },
  },
);

export const fetchCRNAvailability = createAsyncThunk(
  'accounts/fetchCRNAvailability',
  async () => {
    const response = await Client.get<CRNAvailability>('/api/v1/pineapple/crn/available');
    return response.data;
  },
);

/**
 * Assign (or rotate) a BPAY CRN for a Chargebee customer.
 * Identify the customer by customerId, or by email if no id is given.
 * Populates cf_bpay_reference only when empty, unless rotate is true.
 */
export const assignCRN = createAsyncThunk<CRNAssignResult, AssignCRNArgs, { rejectValue: string }>(
  'accounts/assignCRN',
  async (arg, { rejectWithValue }) => {
    try {
      const response = await Client.post<CRNAssignResult>('/api/v1/pineapple/crn/assign', {
        customer_id: arg.customerId ?? '',
        email: arg.email ?? '',
        rotate: arg.rotate ?? false,
      });
      return response.data;
    } catch (e: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      const msg = e?.response?.data?.error ?? 'Failed to assign CRN';
      return rejectWithValue(msg);
    }
  },
);

/**
 * Exact-match server search by CRN, email, or customer id.
 * Avoids scanning the table: CRN hits the SK index, email is resolved via
 * Chargebee first. Returns matching accounts (usually 0 or 1).
 */
export const searchAccounts = createAsyncThunk(
  'accounts/searchAccounts',
  async (query: string) => {
    const url = `/api/v1/admin/accounts/search?q=${encodeURIComponent(query)}`;
    const response = await Client.get<AccountsListWithNext>(url);
    return { query, items: response.data.items ?? [] };
  },
);

export const accountsSlice = createSlice({
  name: 'accounts',
  initialState,
  reducers: {
    clearSearch: (state) => {
      state.searchResults = null;
      state.searchQuery = '';
      state.searchStatus = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchAccounts — resets list
      .addCase(fetchAccounts.pending, (state) => {
        state.accountsStatus = 'loading';
      })
      .addCase(fetchAccounts.fulfilled, (state, action) => {
        state.accountsStatus = 'idle';
        state.accounts = action.payload.items ?? [];
        state.nextToken = action.payload.next ?? null;
      })
      .addCase(fetchAccounts.rejected, (state) => {
        state.accountsStatus = 'failed';
      })

      // fetchMoreAccounts — appends to list
      .addCase(fetchMoreAccounts.pending, (state) => {
        state.accountsStatus = 'loading';
      })
      .addCase(fetchMoreAccounts.fulfilled, (state, action) => {
        state.accountsStatus = 'idle';
        state.accounts = [...state.accounts, ...(action.payload.items ?? [])];
        state.nextToken = action.payload.next ?? null;
      })
      .addCase(fetchMoreAccounts.rejected, (state) => {
        state.accountsStatus = 'failed';
      })

      // fetchServicesForAccount — caches by account id
      .addCase(fetchServicesForAccount.pending, (state, action) => {
        state.servicesStatusById[action.meta.arg] = 'loading';
      })
      .addCase(fetchServicesForAccount.fulfilled, (state, action) => {
        state.servicesStatusById[action.meta.arg] = 'idle';
        state.servicesById[action.meta.arg] = action.payload;
      })
      .addCase(fetchServicesForAccount.rejected, (state, action) => {
        state.servicesStatusById[action.meta.arg] = 'failed';
      })

      // syncAccount — refresh from Chargebee
      .addCase(syncAccount.pending, (state, action) => {
        state.syncStatusById[action.meta.arg] = 'syncing';
      })
      .addCase(syncAccount.fulfilled, (state, action) => {
        const id = action.meta.arg;
        state.syncStatusById[id] = 'idle';
        state.servicesById[id] = action.payload.services ?? [];
        state.servicesStatusById[id] = 'idle';
        // Update last_synced_at on the account in the list
        const idx = state.accounts.findIndex((a) => a.id === id);
        if (idx !== -1) {
          state.accounts[idx] = action.payload.account;
        }
      })
      .addCase(syncAccount.rejected, (state, action) => {
        state.syncStatusById[action.meta.arg] = 'idle';
      })

      // fetchCRNAvailability
      .addCase(fetchCRNAvailability.pending, (state) => {
        state.crnStatus = 'loading';
      })
      .addCase(fetchCRNAvailability.fulfilled, (state, action) => {
        state.crnStatus = 'idle';
        state.crn = action.payload;
      })
      .addCase(fetchCRNAvailability.rejected, (state) => {
        state.crnStatus = 'failed';
      })

      // assignCRN — populate or rotate a customer's CRN
      .addCase(assignCRN.pending, (state) => {
        state.assignStatus = 'loading';
        state.assignError = null;
      })
      .addCase(assignCRN.fulfilled, (state, action) => {
        state.assignStatus = 'idle';
        state.assignResult = action.payload;
        state.assignError = null;
        // A newly assigned/rotated CRN changes pool availability.
        if (state.crn && (action.payload.created || action.payload.rotated)) {
          state.crn = {
            ...state.crn,
            allocated: state.crn.allocated + 1,
            available: Math.max(0, state.crn.available - 1),
          };
        }
      })
      .addCase(assignCRN.rejected, (state, action) => {
        state.assignStatus = 'failed';
        state.assignError = action.payload ?? 'Failed to assign CRN';
      })

      // searchAccounts — exact server-side lookup
      .addCase(searchAccounts.pending, (state, action) => {
        state.searchStatus = 'loading';
        state.searchQuery = action.meta.arg;
      })
      .addCase(searchAccounts.fulfilled, (state, action) => {
        state.searchStatus = 'idle';
        state.searchQuery = action.payload.query;
        state.searchResults = action.payload.items;
      })
      .addCase(searchAccounts.rejected, (state) => {
        state.searchStatus = 'failed';
        state.searchResults = [];
      });
  },
});

export const { clearSearch } = accountsSlice.actions;

export const selectAccounts = (state: RootState) => state.accounts.accounts;
export const selectAccountsStatus = (state: RootState) => state.accounts.accountsStatus;
export const selectNextToken = (state: RootState) => state.accounts.nextToken;
export const selectServicesById = (state: RootState) => state.accounts.servicesById;
export const selectServicesStatusById = (state: RootState) => state.accounts.servicesStatusById;
export const selectSyncStatusById = (state: RootState) => state.accounts.syncStatusById;
export const selectCRN = (state: RootState) => state.accounts.crn;
export const selectCRNStatus = (state: RootState) => state.accounts.crnStatus;
export const selectAssignResult = (state: RootState) => state.accounts.assignResult;
export const selectAssignStatus = (state: RootState) => state.accounts.assignStatus;
export const selectAssignError = (state: RootState) => state.accounts.assignError;
export const selectSearchResults = (state: RootState) => state.accounts.searchResults;
export const selectSearchQuery = (state: RootState) => state.accounts.searchQuery;
export const selectSearchStatus = (state: RootState) => state.accounts.searchStatus;

export default accountsSlice.reducer;
